import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsGeneral';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import api, { API_BASE } from '../../../../../api';
import Modal from './PersonasModal';
import Tabs from './PersonasTabs';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { getSessionEmpresaId } from '../../../../../utils/SessionUser';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Catálogo de Personas";
const endpoint = ENDPOINTS.personas;
const endpointById = ENDPOINTS.personaById;
const modelo = Models.personas;

const maps = {
  mapCreate: (form) => ({
    nombre: form.nombre,
    apellidoPaterno: form.apellidoPaterno,
    apellidoMaterno: form.apellidoMaterno,
    curp: form.curp,
    rfc: form.rfc,
    genero: form.genero,
    fechaNacimiento: form.fechaNacimiento,
    fotoPerfil: form.fotoPerfil,
    idEmpresa: getSessionEmpresaId(), // Contexto jerárquico automático
  }),
  mapUpdate: (id, form) => ({
    idPersona: id,
    nombre: form.nombre,
    apellidoPaterno: form.apellidoPaterno,
    apellidoMaterno: form.apellidoMaterno,
    curp: form.curp,
    rfc: form.rfc,
    genero: form.genero,
    fechaNacimiento: form.fechaNacimiento,
    fotoPerfil: form.fotoPerfil,
    idEmpresa: form.idEmpresa || getSessionEmpresaId(), // Persistencia o contexto
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  // ── Permisos granulares por recurso ──────────────────────────────────────
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions();

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(endpoint);
      setRows((res.data || []).map((x) => modelo(x)));
    } catch (e) {
      console.error(e);
      triggerAlert('Ocurrió un error al cargar información. Inténtelo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const nombre = (r.nombre || '').toLowerCase();
      const ap1 = (r.apellidoPaterno || '').toLowerCase();
      const ap2 = (r.apellidoMaterno || '').toLowerCase();
      const curp = (r.curp || '').toLowerCase();
      const rfc = (r.rfc || '').toLowerCase();
      const empresa = (r.nombreEmpresa || 'n/a').toLowerCase();
      return (
        nombre.includes(q) ||
        ap1.includes(q) ||
        ap2.includes(q) ||
        curp.includes(q) ||
        rfc.includes(q) ||
        empresa.includes(q)
      );
    });
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idPersona === id);
    if (!r) {
      triggerAlert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);

      // 1. Borrar imagen de S3 si aplica
      const person = rows.find(r => r.idPersona === deleteTarget.id);
      if (person && person.fotoPerfil && person.fotoPerfil.startsWith('http')) {
        try {
          const UPLOAD_ENDPOINT = `${API_BASE}/upload/persona`;
          await api.delete(`${UPLOAD_ENDPOINT}?filePath=${encodeURIComponent(person.fotoPerfil)}`);
        } catch (err) {
          console.warn("No se pudo eliminar la foto de perfil en S3:", err);
        }
      }

      // 2. Borrar registro en DB
      await api.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Ocurrió un error al eliminar. Inténtelo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await api.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idPersona ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await api.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || 'Ocurrió un error al guardar. Inténtelo más tarde.';
      triggerAlert(msg);
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por nombre, apellidos, CURP, RFC o empresa…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />

          {puedeCrear && (
            <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
          )}
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Apellido paterno</th>
                  <th>Apellido materno</th>
                  <th>CURP</th>
                  <th>RFC</th>
                  <th>Género</th>
                  <th>Fecha nacimiento</th>
                  <th>Empresa</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idPersona} className={editingRowId === r.idPersona ? 'rk-row-active' : ''}>
                    <td><strong>{normalizeValue(r.nombre)}</strong></td>
                    <td>{normalizeValue(r.apellidoPaterno)}</td>
                    <td>{normalizeValue(r.apellidoMaterno)}</td>
                    <td>{normalizeValue(r.curp)}</td>
                    <td>{normalizeValue(r.rfc)}</td>
                    <td>{normalizeValue(r.genero)}</td>
                    <td>{normalizeValue(r.fechaNacimiento)}</td>
                    <td>
                      <span style={{ fontSize: '0.85rem', color: r.idEmpresa ? '#334155' : '#94a3b8' }}>
                        {r.nombreEmpresa || 'N/A'}
                      </span>
                    </td>
                    {puedeEditar && (
                       <td style={{ textAlign: 'center' }}>
                         <button
                           type="button"
                           className="rk-iconbtn rk-iconbtn--edit"
                           onClick={() => handleEdit(r.idPersona)}
                           aria-label="Editar"
                           title="Editar"
                         >
                           <img src={sigmaEdit} alt="" className="rk-ico" />
                         </button>
                       </td>
                     )}
                     {puedeEliminar && (
                       <td style={{ textAlign: 'center' }}>
                         <button
                           type="button"
                           className="rk-iconbtn rk-iconbtn--del"
                           onClick={() => {
                             setEditingRowId(null);
                             const name = [r.nombre, r.apellidoPaterno, r.apellidoMaterno]
                               .filter(Boolean)
                               .join(' ');
                             askDelete(r.idPersona, name || 'Persona');
                           }}
                           aria-label="Borrar"
                           title="Borrar"
                         >
                           <img src={sigmaDelete} alt="" className="rk-ico" />
                         </button>
                       </td>
                     )}
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                   <tr><td colSpan={8 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn
              label="‹"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            />
            {pageNumbers.map(n => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            {modalMode === 'new' && (
              <div className="person-tabs-disabled" style={{ marginBottom: 12 }}>
                <span>
                  Selecciona o crea una persona y pulsa <b>Editar</b> para gestionar sus datos
                  relacionados.
                </span>
              </div>
            )}
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
            {modalOpen && modalMode === 'edit' && editData?.idPersona > 0 && (
              <Tabs
                personId={editData.idPersona}
                puedeCrear={puedeCrear}
                puedeEditar={puedeEditar}
                puedeEliminar={puedeEliminar}
              />
            )}
          </div>
        )}

      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
